"use strict";
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size
} from "@floating-ui/dom";
import { topLayerOverTransforms } from "./topLayerOverTransforms.dev.js";
function roundByDPR(num) {
  if (typeof num === "undefined")
    return 0;
  const dpr = window.devicePixelRatio || 1;
  return Math.round(num * dpr) / dpr;
}
const REQUIRED_DISTANCE_TO_EDGE = 8;
const MIN_OVERLAY_HEIGHT = 100;
const getFallbackPlacements = (placement) => {
  var _a;
  const fallbacks = {
    left: ["right", "bottom", "top"],
    "left-start": ["right-start", "bottom", "top"],
    "left-end": ["right-end", "bottom", "top"],
    right: ["left", "bottom", "top"],
    "right-start": ["left-start", "bottom", "top"],
    "right-end": ["left-end", "bottom", "top"],
    top: ["bottom", "left", "right"],
    "top-start": ["bottom-start", "left", "right"],
    "top-end": ["bottom-end", "left", "right"],
    bottom: ["top", "left", "right"],
    "bottom-start": ["top-start", "left", "right"],
    "bottom-end": ["top-end", "left", "right"]
  };
  return (_a = fallbacks[placement]) != null ? _a : [placement];
};
export const placementUpdatedSymbol = Symbol("placement updated");
export class PlacementController {
  constructor(host) {
    this.originalPlacements = /* @__PURE__ */ new WeakMap();
    this.allowPlacementUpdate = false;
    this.updatePlacement = () => {
      if (!this.allowPlacementUpdate && this.options.type !== "modal" && this.cleanup) {
        this.target.dispatchEvent(new Event("close", { bubbles: true }));
        return;
      }
      this.computePlacement();
      this.allowPlacementUpdate = false;
    };
    this.resetOverlayPosition = () => {
      if (!this.target || !this.options)
        return;
      this.target.style.removeProperty("max-height");
      this.target.style.removeProperty("height");
      this.initialHeight = void 0;
      this.isConstrained = false;
      this.host.offsetHeight;
      this.computePlacement();
    };
    this.host = host;
    this.host.addController(this);
  }
  async placeOverlay(target = this.target, options = this.options) {
    this.target = target;
    this.options = options;
    if (!target || !options)
      return;
    const cleanup = autoUpdate(
      options.trigger,
      target,
      this.updatePlacement,
      {
        elementResize: false,
        layoutShift: false
      }
    );
    this.cleanup = () => {
      var _a;
      (_a = this.host.elements) == null ? void 0 : _a.forEach((element) => {
        element.addEventListener(
          "sp-closed",
          () => {
            const placement = this.originalPlacements.get(element);
            if (placement) {
              element.setAttribute("placement", placement);
            }
            this.originalPlacements.delete(element);
          },
          { once: true }
        );
      });
      cleanup();
    };
  }
  async computePlacement() {
    var _a, _b;
    const { options, target } = this;
    await (document.fonts ? document.fonts.ready : Promise.resolve());
    const flipMiddleware = !(options.trigger instanceof HTMLElement) ? flip({
      padding: REQUIRED_DISTANCE_TO_EDGE,
      fallbackPlacements: getFallbackPlacements(options.placement)
    }) : flip();
    const [mainAxis = 0, crossAxis = 0] = Array.isArray(options == null ? void 0 : options.offset) ? options.offset : [options.offset, 0];
    const tipElement = (_a = this.host.elements.find(
      (el) => el.tipElement
    )) == null ? void 0 : _a.tipElement;
    const middleware = [
      offset({
        mainAxis,
        crossAxis
      }),
      shift({ padding: REQUIRED_DISTANCE_TO_EDGE }),
      flipMiddleware,
      size({
        padding: REQUIRED_DISTANCE_TO_EDGE,
        apply: ({
          availableWidth,
          availableHeight,
          rects: { floating }
        }) => {
          const maxHeight = Math.max(
            MIN_OVERLAY_HEIGHT,
            Math.floor(availableHeight)
          );
          const actualHeight = floating.height;
          this.initialHeight = !this.isConstrained ? actualHeight : this.initialHeight || actualHeight;
          this.isConstrained = actualHeight < this.initialHeight || maxHeight <= actualHeight;
          const appliedHeight = this.isConstrained ? `${maxHeight}px` : "";
          Object.assign(target.style, {
            maxWidth: `${Math.floor(availableWidth)}px`,
            maxHeight: appliedHeight,
            height: appliedHeight
          });
        }
      }),
      ...tipElement ? [
        arrow({
          element: tipElement,
          padding: options.tipPadding || REQUIRED_DISTANCE_TO_EDGE
        })
      ] : [],
      topLayerOverTransforms()
    ];
    const { x, y, placement, middlewareData } = await computePosition(
      options.trigger,
      target,
      {
        placement: options.placement,
        middleware,
        strategy: "fixed"
      }
    );
    Object.assign(target.style, {
      top: "0px",
      left: "0px",
      translate: `${roundByDPR(x)}px ${roundByDPR(y)}px`
    });
    target.setAttribute("actual-placement", placement);
    (_b = this.host.elements) == null ? void 0 : _b.forEach((element) => {
      this.originalPlacements.set(
        element,
        element.getAttribute("placement")
      );
      element.setAttribute("placement", placement);
    });
    if (tipElement && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      Object.assign(tipElement.style, {
        top: placement.startsWith("right") || placement.startsWith("left") ? "0px" : "",
        left: placement.startsWith("bottom") || placement.startsWith("top") ? "0px" : "",
        translate: `${roundByDPR(arrowX)}px ${roundByDPR(arrowY)}px`
      });
    }
  }
  hostConnected() {
    document.addEventListener(
      "sp-update-overlays",
      this.resetOverlayPosition
    );
  }
  hostUpdated() {
    var _a;
    if (!this.host.open) {
      (_a = this.cleanup) == null ? void 0 : _a.call(this);
      this.cleanup = void 0;
    }
  }
  hostDisconnected() {
    var _a;
    (_a = this.cleanup) == null ? void 0 : _a.call(this);
    this.cleanup = void 0;
    document.removeEventListener(
      "sp-update-overlays",
      this.resetOverlayPosition
    );
  }
}
//# sourceMappingURL=PlacementController.dev.js.map
